import React from 'react';
import { Device } from "../../types/device";

type Props = {
    devices: Device[];
};

export default function ComparisonSpecifications({ devices }: Props) {
    const numDevices = devices.length;
    const specGroups = [
        {
            title: "Screen",
            items: [
                { label: "Screen Size", key: (d: Device) => d.screen_size_inch ? `${d.screen_size_inch}''` : null },
                { label: "Resolution", key: (d: Device) => d.screen_resolution },
                { label: "Pixel Density", key: (d: Device) => d.ppi ? `${d.ppi} ppi` : null },
                { label: "Panel Type", key: (d: Device) => d.screen_type },
                { label: "Refresh Rate", key: (d: Device) => d.refresh_rate_hz ? `${d.refresh_rate_hz} Hz` : null },
                { label: "HDR Support", key: (d: Device) => d.hdr_support ? "Yes" : "No" },
                { label: "Protection", key: (d: Device) => d.glass_type },
                { label: "Other", key: (d: Device) => d.screen_other },
            ]
        },
        {
            title: "Performance",
            items: [
                { label: "CPU Model", key: (d: Device) => d.cpu_model },
                { label: "CPU Speed", key: (d: Device) => d.cpu_frequency_ghz ? `${d.cpu_frequency_ghz} GHz` : null },
                { label: "CPU Cores", key: (d: Device) => d.cpu_cores },
                { label: "GPU", key: (d: Device) => d.gpu_model },
                { label: "RAM", key: (d: Device) => d.ram_gb ? `${d.ram_gb} GB` : null },
                { label: "Storage", key: (d: Device) => d.storage_gb?.map(s => `${s}GB`).join(", ") },
                { label: "Card Slot", key: (d: Device) => d.memory_slot === 1 ? "Yes" : "No" },
            ]
        },
        {
            title: "Main Camera",
            items: [
                { label: "Resolution", key: (d: Device) => d.main_camera_mp ? `${d.main_camera_mp} MP` : null },
                { label: "Aperture", key: (d: Device) => d.main_camera_aperture },
                { label: "Focal Length", key: (d: Device) => d.main_camera_focal_length_mm ? `${d.main_camera_focal_length_mm} mm` : null },
                { label: "Ultrawide", key: (d: Device) => d.ultrawide_camera_mp ? `${d.ultrawide_camera_mp} MP` : null },
                { label: "Telephoto", key: (d: Device) => d.telephoto_camera_mp ? `${d.telephoto_camera_mp} MP` : null },
                { label: "Zoom", key: (d: Device) => d.camera_zoom_optical },
                { label: "Video", key: (d: Device) => d.video_resolution },
            ]
        },
        {
            title: "Front Camera",
            items: [
                { label: "Resolution", key: (d: Device) => d.front_camera_mp ? `${d.front_camera_mp} MP` : null },
                { label: "Aperture", key: (d: Device) => d.front_camera_aperture },
                { label: "Field of View", key: (d: Device) => d.front_camera_fov_deg ? `${d.front_camera_fov_deg}°` : null },
            ]
        },
        {
            title: "Connectivity",
            items: [
                { label: "Wi-Fi & BT", key: (d: Device) => [d.wifi_version, d.bluetooth_version].filter(Boolean).join(", ") },
                { label: "NFC", key: (d: Device) => d.nfc ? "Yes" : "No" },
                { label: "GPS", key: (d: Device) => d.gps ? "Yes" : "No" },
                { label: "Port", key: (d: Device) => d.charging_port },
                { label: "Biometrics", key: (d: Device) => [d.face_scanner && "Face", d.fingerprint_scanner && "Fingerprint"].filter(Boolean).join(", ") },
                { label: "Durability", key: (d: Device) => d.waterproof },
            ]
        },
        {
            title: "Battery",
            items: [
                { label: "Capacity", key: (d: Device) => d.battery_capacity_mah ? `${d.battery_capacity_mah} mAh` : null },
                { label: "Charging", key: (d: Device) => d.charger_power_w ? `${d.charger_power_w} W` : null },
                { label: "Wireless", key: (d: Device) => d.wireless_charging ? "Yes" : "No" },
            ]
        },
        {
            title: "General Info",
            items: [
                { label: "Release Date", key: (d: Device) => d.release_date },
                { label: "OS", key: (d: Device) => d.os },
                { label: "Weight", key: (d: Device) => d.weight_g ? `${d.weight_g} g` : null },
                { label: "Dimensions", key: (d: Device) => d.length ? `${d.length}x${d.width}x${d.thickness}mm` : null },
                { label: "SIM / eSIM", key: (d: Device) => `${d.sim_slots} / ${d.e_sim ? "Yes" : "No"}` },
            ]
        }
    ];

    return (
        <div className="flex flex-col gap-20 mt-16 mb-32">
            {specGroups.map((group) => {
                // Modified logic: If devices is empty, show ALL items.
                // If devices exist, filter to only show items that have data.
                const visibleItems = group.items.filter(item =>
                    numDevices === 0 ? true : devices.some(d => !!item.key(d))
                );

                // If a group has no visible items even after the check, skip it
                if (visibleItems.length === 0) return null;

                return (
                    <div key={group.title} className="grid grid-cols-12 gap-6">
                        {/* Sidebar (Labels) */}
                        <div className="col-span-3">
                            <div className="inline-block border-b-2 border-blue-600 pb-1 mb-6">
                                <h2 className="text-2xl font-bold text-slate-900">{group.title}</h2>
                            </div>
                            <div className="flex flex-col">
                                {visibleItems.map((item, idx) => (
                                    <div key={idx} className="min-h-[4rem] flex items-center text-slate-500 font-medium text-sm leading-tight">
                                        {item.label}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Values Content */}
                        <div className="col-span-9 flex flex-col pt-[4.8rem]">
                            {/* If no devices, show a placeholder row or empty space to keep alignment */}
                            {numDevices === 0 ? (
                                visibleItems.map((_, idx) => (
                                    <div key={idx} className="min-h-[4rem] border-b border-slate-100 last:border-0" />
                                ))
                            ) : (
                                visibleItems.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="grid gap-6 min-h-[4rem] border-b border-slate-100 last:border-0 items-center"
                                        style={{ gridTemplateColumns: `repeat(${numDevices}, 240px)` }}
                                    >
                                        {devices.map((device, dIdx) => (
                                            <div key={dIdx} className="flex justify-center">
                                                <div className="w-full max-w-[240px] text-center font-bold text-slate-900 text-[15px] px-1 break-words">
                                                    {item.key(device) || "—"}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}